import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IndianRupee, Users, Clock, Search, Play, Eye,
  CheckCircle2, RefreshCw,
} from 'lucide-react';
import { useApi } from '../../lib/api';
import Sparkline from './components/Sparkline';

const fmtINR = (paise) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
    .format((paise || 0) / 100);

function timeAgo(iso) {
  if (!iso) return 'never';
  const normalized = iso.includes('Z') || iso.includes('+') ? iso : iso.replace(' ', 'T') + 'Z';
  const t = new Date(normalized).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1)   return 'just now';
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function DonutChart({ slices, size = 120 }) {
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={size/2 - 8} fill="none" stroke="#e5e7eb" strokeWidth={16} />
        <text x={size/2} y={size/2 + 5} textAnchor="middle" fill="#9ca3af" fontSize={13} fontWeight={700}>0</text>
      </svg>
    );
  }
  const r = size / 2 - 8;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const arcs = slices.map((sl) => {
    const frac = sl.value / total;
    const dash = frac * circ;
    const arc = { ...sl, dash, offset };
    offset += dash;
    return arc;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      {arcs.map((arc, i) => (
        <circle
          key={i}
          cx={size/2} cy={size/2} r={r}
          fill="none"
          stroke={arc.color}
          strokeWidth={16}
          strokeDasharray={`${arc.dash} ${circ - arc.dash}`}
          strokeDashoffset={-arc.offset}
        />
      ))}
    </svg>
  );
}

function RevenuePeriodCard({ revenue, period, setPeriod }) {
  const series = (revenue.series || []).map((p) => ({ label: p.bucket.slice(5), value: p.revenue_paise }));
  return (
    <div className="admin-card" style={{ marginBottom: 0 }}>
      <div className="admin-card-header">
        <h2>Revenue — last 30 days</h2>
        <select
          value={period}
          onChange={e => setPeriod(e.target.value)}
          style={{
            font: 'inherit', fontSize: 13, padding: '5px 28px 5px 10px', border: '1px solid #d1d5db',
            borderRadius: 8, appearance: 'none', background: `white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 8px center`,
            color: '#374151', cursor: 'pointer',
          }}
        >
          <option value="day">Daily</option>
          <option value="week">Weekly</option>
          <option value="month">Monthly</option>
        </select>
      </div>
      <Sparkline data={series} valueFormat={fmtINR} />
    </div>
  );
}

export default function AdminHome() {
  const api = useApi();
  const [status, setStatus]       = useState(null);
  const [revenue, setRevenue]     = useState(null);
  const [tenants, setTenants]     = useState([]);
  const [paySummary, setPaySummary] = useState(null);
  const [overLimit, setOverLimit] = useState([]);
  const [payIssues, setPayIssues] = useState([]);
  const [error, setError]         = useState(null);
  const [busy, setBusy]           = useState({});
  const [loading, setLoading]     = useState(false);
  const [period, setPeriod]       = useState('day');
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [s, r, t, ps, ol, pi] = await Promise.all([
        api.adminReconStatus(),
        api.adminRevenue({ granularity: period }),
        api.adminTenants(),
        api.adminPaymentSummary(),
        api.adminTenantsOver(),
        api.adminTenantsPayIssues(),
      ]);
      setStatus(s); setRevenue(r); setTenants(t.tenants || []);
      setPaySummary(ps); setOverLimit(ol.tenants || []); setPayIssues(pi.tenants || []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, period]);

  useEffect(() => { load(); }, [load]);

  const triggerRun = async (type) => {
    setBusy((b) => ({ ...b, [type]: true }));
    try {
      await api.adminTriggerRun(type);
      await load();
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setBusy((b) => ({ ...b, [type]: false }));
    }
  };

  if (!status || !revenue || !paySummary) {
    return <div style={{ color: '#9ca3af', padding: 32 }}>Loading…</div>;
  }

  const activeTenants = tenants.filter((t) => t.billing_status === 'active').length;
  const pastDue       = tenants.filter((t) => t.billing_status === 'past_due' || t.billing_status === 'suspended').length;
  const openTotal     = Object.values(status.openBySeverity || {}).reduce((s, n) => s + n, 0);
  const openCritical  = (status.openBySeverity || {}).critical || 0;
  const attention     = mergeTenants(overLimit, payIssues);

  const paySlices = [
    { label: 'Successful',         value: paySummary.totals.success,             color: '#22c55e' },
    { label: 'Failed',             value: paySummary.totals.failed,              color: '#ef4444' },
    { label: 'Pending recon',      value: paySummary.pendingReconciliation,       color: '#f97316' },
    { label: 'No data',            value: Math.max(0, paySummary.totals.total - paySummary.totals.success - paySummary.totals.failed - paySummary.pendingReconciliation), color: '#d1d5db' },
  ];


  return (
    <>
      {/* Page header */}
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Overview</h1>
          <p className="admin-page-subtitle">Reconciliation status, financial pulse, and tenant health.</p>
        </div>
        <div className="admin-header-actions">
          {lastUpdated && (
            <span className="admin-header-meta">
              Last updated: {timeAgo(lastUpdated.toISOString())}
            </span>
          )}
          <button className="btn-secondary" onClick={load} disabled={loading} style={{ gap: 6 }}>
            <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="admin-banner error">{error}</div>}

      {/* Recon cards */}
      <div className="admin-recon-row">
        {['payment', 'usage'].map((t) => {
          const r = status.latestRuns?.[t];
          const pillClass = r
            ? (r.status === 'succeeded' ? 'succeeded' : r.status === 'running' ? 'running' : 'failed')
            : 'skipped';
          const pillLabel = r
            ? (r.status.charAt(0).toUpperCase() + r.status.slice(1))
            : 'Never Run';
          return (
            <div key={t} className="admin-recon-card">
              <div className="admin-recon-card-header">
                <h3>{t.charAt(0).toUpperCase() + t.slice(1)} reconciliation</h3>
                <span className={`admin-status-pill ${pillClass}`}>{pillLabel}</span>
              </div>
              <div className="admin-recon-meta">
                Last run: {r ? timeAgo(r.started_at) : '—'}
                {r ? ` · scanned ${r.scanned_count} · ${r.finding_count} findings` : ''}
              </div>
              <div className="admin-recon-meta">
                Open findings (this job type): <strong>{(status.openByJobType?.[t]) || 0}</strong>
              </div>
              <div className="admin-recon-actions">
                <button className="btn-primary" disabled={busy[t]} onClick={() => triggerRun(t)}>
                  <Play size={11} />
                  {busy[t] ? 'Running…' : 'Run now'}
                </button>
                <Link to={`/admin/reconciliation?type=${t}`} className="btn-secondary">
                  <Eye size={12} />View
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* KPI tiles */}
      <div className="admin-kpi-row">
        <div className="admin-kpi">
          <div className="admin-kpi-icon blue"><IndianRupee size={18} /></div>
          <p className="admin-kpi-label">Revenue (last 30d)</p>
          <p className="admin-kpi-value">{fmtINR(revenue.totals?.revenue_paise)}</p>
          <p className="admin-kpi-sub">{revenue.totals?.payments || 0} captured payments</p>
        </div>
        <div className="admin-kpi ok">
          <div className="admin-kpi-icon green"><Users size={18} /></div>
          <p className="admin-kpi-label">Active tenants</p>
          <p className="admin-kpi-value">{activeTenants}</p>
          <p className="admin-kpi-sub">{tenants.length} total</p>
        </div>
        <div className={`admin-kpi ${pastDue > 0 ? 'warn' : ''}`}>
          <div className="admin-kpi-icon orange"><Clock size={18} /></div>
          <p className="admin-kpi-label">Past-due / Suspended</p>
          <p className="admin-kpi-value">{pastDue}</p>
          <p className="admin-kpi-sub">Need follow-up</p>
        </div>
        <div className={`admin-kpi ${openCritical > 0 ? 'danger' : (openTotal > 0 ? 'warn' : '')}`}>
          <div className={`admin-kpi-icon ${openCritical > 0 ? 'red' : 'purple'}`}><Search size={18} /></div>
          <p className="admin-kpi-label">Open Findings</p>
          <p className="admin-kpi-value">{openTotal}</p>
          <p className="admin-kpi-sub">{openCritical} critical</p>
        </div>
      </div>

      {/* Revenue chart + Payment health side by side */}
      <div className="admin-card-grid">
        <RevenuePeriodCard revenue={revenue} period={period} setPeriod={setPeriod} />

        <div className="admin-card" style={{ marginBottom: 0 }}>
          <h2 style={{ marginBottom: 16 }}>Payment health (last 30 days)</h2>

          {/* Mini KPI row */}
          <div className="admin-payment-kpi-grid">
            {[
              { label: 'Total',        value: paySummary.totals.total,              icon: '📄', bg: '#f9fafb' },
              { label: 'Successful',   value: paySummary.totals.success,            icon: '✅', bg: '#f0fdf4' },
              { label: 'Failed',       value: paySummary.totals.failed,             icon: '❌', bg: '#fef2f2' },
              { label: 'Pending Recon',value: paySummary.pendingReconciliation,      icon: '🕐', bg: '#fff7ed' },
            ].map(k => (
              <div key={k.label} className="admin-payment-kpi">
                <div className="admin-payment-kpi-icon" style={{ background: k.bg, fontSize: 16 }}>{k.icon}</div>
                <div>
                  <div className="admin-payment-kpi-label">{k.label}</div>
                  <div className="admin-payment-kpi-value">{k.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Donut + legend */}
          <div className="admin-donut-wrap">
            <DonutChart slices={paySlices} size={110} />
            <div className="admin-donut-legend">
              {paySlices.map((sl) => {
                const total = paySummary.totals.total || 1;
                const pct = Math.round((sl.value / total) * 100);
                return (
                  <div key={sl.label} className="admin-donut-legend-item">
                    <div className="admin-donut-legend-label">
                      <div className="admin-donut-dot" style={{ background: sl.color }} />
                      {sl.label}
                    </div>
                    <div>
                      <span className="admin-donut-value">{sl.value}</span>
                      <span className="admin-donut-pct">({pct}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Tenants needing attention */}
      <div className="admin-card" style={{ marginTop: 16 }}>
        <h2>Tenants needing attention ({attention.length})</h2>
        {attention.length === 0 ? (
          <div className="admin-attention-empty">
            <div className="admin-attention-check">
              <CheckCircle2 size={26} />
            </div>
            <div className="admin-attention-title">All tenants healthy.</div>
            <div className="admin-attention-sub">Great job! No tenant needs your attention right now.</div>
          </div>
        ) : (
          <div className="admin-table-scroll"><table className="admin-table">
            <thead>
              <tr>
                <th>App</th><th>Owner</th><th>Plan</th><th>Status</th><th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {attention.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600, color: '#0f1c2e' }}>{t.name}</td>
                  <td style={{ color: '#6b7280' }}>{t.owner_email || '—'}</td>
                  <td>{t.plan_code || '—'}</td>
                  <td>
                    <span className={`admin-status-pill ${pillForBilling(t.billing_status)}`}>
                      {(t.billing_status || '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </span>
                  </td>
                  <td style={{ color: '#6b7280', fontSize: 12 }}>{t.reasons.join(' + ')}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

function pillForBilling(s) {
  if (s === 'active')    return 'ok';
  if (s === 'past_due')  return 'partial';
  if (s === 'suspended') return 'failed';
  if (s === 'cancelled') return 'failed';
  return 'skipped';
}

function mergeTenants(over, issues) {
  const map = new Map();
  for (const t of over)   map.set(t.id, { ...t, reasons: ['over user limit'] });
  for (const t of issues) {
    if (map.has(t.id)) map.get(t.id).reasons.push('payment issue');
    else               map.set(t.id, { ...t, reasons: ['payment issue'] });
  }
  return [...map.values()];
}
